import { AccountTypes } from '@interfaces/models/account-types';
import { CostBasisMethods } from '@interfaces/models/cost-basis-methods';
import { AvgCostConversion, MinTaxConversion } from '@interfaces/models/spec-id-fallback.enum';
import {
  AccountTypesToFullMap,
  EmploymentStatusCodeMap,
  TransactionTypesMap,
  TransferTypeToFullMap,
} from '@interfaces/submit/internal-transfer-co0-payload.interface';
import { createSelector } from '@ngrx/store';
import { Move_To_New } from '@shared/models';
import { selectIncomeNetWorth } from '@store/selectors/income-net-worth/income-net-worth.selectors';
import {
  selectLetterFilesMetadata,
  selectVGHasLetter,
} from '@store/selectors/letter-upload/letter-upload.selectors';
import { selectMfePersonalInfoClientPersonalInfo } from '@store/selectors/mfe-personal-info-client/mfe-personal-info-client.selectors';
import { selectEmploymentMfeData } from '@store/selectors/open-account/open-account.selectors';
import { selectOTPVerificationStatus } from '@store/selectors/otp/otp.selectors';
import { selectClientName } from '@store/selectors/personal-data/personal-data.selectors';
import { selectMfePersonalAddress } from '@store/selectors/personal-info-address/personal-info-address.selectors';
import { selectMfePhone } from '@store/selectors/personal-phone/personal-phone.selectors';
import type {
  Address as CO0Address,
  ContactInformation,
  CostBasisPutRequestData,
  Employment,
  FromAccount,
  InternalTransferPayload,
} from 'coo-transactions-common-library/dist/models';
import { SMNv2CostBasisMethods } from 'coo-transactions-common-library/dist/models/cost-basis.interface';
import { DefectReasonCode } from 'coo-transactions-common-library/dist/models/defect-reason-code';
import { StateCode } from 'coo-transactions-common-library/dist/models/state-code';
import { TransferType } from 'coo-transactions-common-library/dist/models/transfer-types';

import { calculatePositionsSubtotal } from '../multi-internal-transfer.helpers';
import {
  selectAllMultiITToAccounts,
  selectMultiITFromAvgCostConversion,
  selectMultiITFromMinTaxConversion,
  selectMultiITFromSelectedAccount,
  selectMultiITFromTransferMethod,
  selectMultiITTransferType,
  selectMultiITTriageIsDivorce,
  selectMultiITTriageTransferFrom,
} from '../multi-internal-transfer.selectors';
import {
  buildInvestmentGoals,
  mapPositions,
  resolveToAccountType,
} from './multi-it-submit.helpers';

export const selectMultiITCO0SharedFromAccount = createSelector(
  selectMultiITFromSelectedAccount,
  selectMultiITTriageTransferFrom,
  selectMultiITTransferType,
  selectMultiITFromTransferMethod,
  selectMultiITFromAvgCostConversion,
  selectMultiITFromMinTaxConversion,
  selectMultiITTriageIsDivorce,
  (
    selectedAccount,
    triageFrom,
    transferType,
    transferMethod,
    avgCost,
    minTax,
    isDivorceTransfer,
  ): Omit<FromAccount, 'positions' | 'totalInternalTransferValue' | 'percentageOfHoldings'> & {
    accountName?: string;
    accountNumber?: string;
    dateDisplay?: string;
    productType?: string;
    isDivorceTransfer?: string;
  } => ({
    accountId: selectedAccount?.accountData?.accountId,
    accountName: selectedAccount?.accountData?.accountName,
    accountNumber: selectedAccount?.accountNumber,
    dateDisplay: selectedAccount?.accountData?.dateDisplay,
    productType: selectedAccount?.accountData?.productType,
    accountType: AccountTypesToFullMap[triageFrom],
    internalTransferType: TransferTypeToFullMap[transferType],
    internalTransferMethod: transferMethod,
    convertAverageCostHoldings: avgCost === AvgCostConversion.FIFO,
    convertMinTaxHoldings: minTax === MinTaxConversion.FIFO,
    convertSpecIdHoldings: false,
    isDivorceTransfer: isDivorceTransfer ?? undefined,
  }),
);

export const selectMultiITSubmitCostBasisPutRequest = createSelector(
  selectMultiITFromSelectedAccount,
  selectMultiITFromAvgCostConversion,
  selectMultiITFromMinTaxConversion,
  selectAllMultiITToAccounts,
  (
    fromAccount,
    avgCostConversion,
    minTaxConversion,
    toAccounts,
  ): CostBasisPutRequestData | undefined => {
    const needsAvgCostConvert = avgCostConversion === AvgCostConversion.FIFO;
    const needsMinTaxConvert = minTaxConversion === MinTaxConversion.FIFO;

    if (!needsAvgCostConvert && !needsMinTaxConvert) {
      return undefined;
    }

    const positionsToConvert = toAccounts
      .flatMap((account) => account.positions)
      .filter((position) => {
        if (
          needsAvgCostConvert &&
          position.selectedCostBasisMethod === CostBasisMethods.AVERAGE_COST
        ) {
          return true;
        }
        if (needsMinTaxConvert && position.selectedCostBasisMethod === CostBasisMethods.MIN_TAX) {
          return true;
        }
        return false;
      });

    if (positionsToConvert.length === 0 || fromAccount?.accountData?.accountId === undefined) {
      return undefined;
    }

    return {
      accounts: [
        {
          accountId: fromAccount?.accountData?.accountId,
          positions: positionsToConvert.map((position) => ({
            positionId: position.positionId,
            defaultMethod: SMNv2CostBasisMethods.FIRST_IN_FIRST_OUT,
          })),
        },
      ],
    };
  },
);

const StateCodeMap: Record<string, StateCode> = Object.fromEntries(
  Object.values(StateCode).map((code) => [code, code]),
) as Record<string, StateCode>;

const mapMFEAddressToCO0 = (address?: {
  street: string;
  city: string;
  state: string;
  zipCode: string;
}): CO0Address => ({
  addressLine1: address?.street ?? '',
  cityName: address?.city ?? '',
  stateCode: StateCodeMap[address?.state ?? ''],
  zipCode: address?.zipCode ?? '',
  defectReasonCode: DefectReasonCode.NODF,
  zipCodePlusFour: '',
});

export const selectMultiITCO0BeneficiaryContact = createSelector(
  selectMfePersonalAddress,
  selectMfePhone,
  (personalAddress, phone): ContactInformation | undefined => {
    if (!personalAddress && !phone) {
      return undefined;
    }

    return {
      mailingAddress: mapMFEAddressToCO0(personalAddress?.mailingAddress),
      streetAddress: mapMFEAddressToCO0(personalAddress?.streetAddress),
      primaryPhone: {
        phone: phone?.phoneNumber ?? '',
        mobile: phone?.isMobile ?? false,
      },
    };
  },
);

export const selectMultiITCO0BeneficiaryEmployment = createSelector(
  selectEmploymentMfeData,
  selectVGHasLetter,
  selectLetterFilesMetadata,
  (
    employmentInfo,
    vgHasLetter,
    letterFilesMetadata,
  ): (Employment & { approvalLetterFileName?: string }) | undefined => {
    if (!employmentInfo?.[0]) {
      return undefined;
    }

    const { employmentStatus: statusDetails, associations } = employmentInfo[0];
    const hasAssociatedLetter =
      associations?.associated && (vgHasLetter || (letterFilesMetadata?.length ?? 0) > 0);
    const approvalLetterFileName =
      associations?.associated && !vgHasLetter ? letterFilesMetadata?.[0]?.fileName : undefined;

    return {
      employmentStatus: EmploymentStatusCodeMap[statusDetails?.employmentStatus ?? ''],
      name: statusDetails?.employer ?? '',
      occupation: statusDetails?.occupation ?? '',
      address: mapMFEAddressToCO0(statusDetails?.streetAddress),
      associated: associations?.associated ?? false,
      control: associations?.control ?? false,
      spouse: associations?.spouse ?? false,
      controlCompanyTickers: [...(associations?.controlCompanyTicker ?? [])].map((t) =>
        t.toUpperCase(),
      ),
      controlCompanyNames: [...(associations?.controlCompanyName ?? [])],
      controlCompanyManualEntry: [false],
      ...(hasAssociatedLetter && { associatedPersonApprovalLetter: vgHasLetter }),
      ...(approvalLetterFileName && { approvalLetterFileName }),
    };
  },
);

export const selectMultiITCO0BeneficiaryPersonalInfo = createSelector(
  selectMfePersonalInfoClientPersonalInfo,
  (personalInfo) =>
    personalInfo
      ? {
          email: personalInfo.email || undefined,
          birthDate: personalInfo.birthDate || undefined,
          citizenshipStatus: personalInfo.citizenshipStatus || undefined,
          countryOfCitizenship: personalInfo.countryOfCitizenship || undefined,
          ssn: personalInfo.ssn || undefined,
        }
      : undefined,
);

export const selectMultiITCO0Payloads = createSelector(
  selectAllMultiITToAccounts,
  selectMultiITCO0SharedFromAccount,
  selectMultiITFromTransferMethod,
  selectMultiITSubmitCostBasisPutRequest,
  selectIncomeNetWorth,
  selectMultiITCO0BeneficiaryContact,
  selectMultiITCO0BeneficiaryEmployment,
  selectOTPVerificationStatus,
  selectClientName,
  selectMultiITCO0BeneficiaryPersonalInfo,
  (
    accounts,
    sharedFromAccount,
    transferMethod,
    costBasisPutRequestData,
    financials,
    contact,
    employment,
    otpVerificationStatus,
    clientName,
    personalInfo,
  ): (Omit<InternalTransferPayload, 'beneficiary'> & {
    ownerName?: string;
    beneficiary: InternalTransferPayload['beneficiary'] & {
      personalInfo?: ReturnType<typeof selectMultiITCO0BeneficiaryPersonalInfo>;
    };
  })[] => {
    // Transform each MultiITToAccount into an InternalTransferPayload
    return accounts.map((account, index) => {
      const investmentGoals = buildInvestmentGoals(account.openAccount?.objectives, financials);

      return {
        transferType: TransferType.INTERNAL_TRANSFER,
        ownerName: clientName || '',
        otpVerificationStatus: otpVerificationStatus,
        fromAccount: {
          ...sharedFromAccount,
          positions: mapPositions(account.positions),
          totalInternalTransferValue: calculatePositionsSubtotal(account.positions, transferMethod),
          percentageOfHoldings: account.percentageOfHoldings,
        },
        toAccount: {
          accountId:
            'accountData' in account.selectedAccount
              ? account.selectedAccount.accountData?.accountId
              : undefined,
          transactionType: TransactionTypesMap[account.transactionType],
          accountType: resolveToAccountType(
            account.transactionType,
            'accountData' in account.selectedAccount
              ? account.selectedAccount.accountData.accountType
              : undefined,
          ),
          productType:
            'accountData' in account.selectedAccount
              ? account.selectedAccount.accountData.productType
              : undefined,
          settlementFund: account.openAccount?.settlementFund ?? undefined,
          accountName:
            'accountData' in account.selectedAccount
              ? account.selectedAccount.accountData?.accountName
              : account.selectedAccount.accountName,
          accountNumber: account.selectedAccount.accountNumber,
        },
        beneficiary: {
          ownsToAccount:
            Move_To_New.has(account.transactionType) || 'accountData' in account.selectedAccount, //The account is their account if it has an ID (not written in) or is new account
          ...(investmentGoals ? { investmentGoals } : {}),
          ...(account.openAccount && contact ? { contact } : {}),
          ...(account.openAccount && employment ? { employment } : {}),
          ...(account.openAccount && personalInfo ? { personalInfo } : {}),
        },
        ...(index === 0 && costBasisPutRequestData
          ? { costBasisData: costBasisPutRequestData }
          : {}),
      };
    });
  },
);
